import { Suspense, useEffect, useMemo, useRef, useState, useTransition } from "react"
import { Chart } from "react-chartjs-2"
import { useTranslation } from "react-i18next"
import { PreloadedQuery } from "react-relay"
import { CaretLeft, CaretRight } from "@phosphor-icons/react"
import {
  BarController,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  LinearScale,
  LineController,
  LineElement,
  PointElement,
  Title,
} from "chart.js"
import { LoadingSpinner } from "@/components/loading-spinner"
import { Button } from "@/components/ui/button"
import { DatePicker, VISUAL_DATE_FORMAT } from "@/components/ui/date-picker"
import { ScrollArea } from "@/components/ui/scroll-area"
import { DateOnly } from "@/lib/date-only"
import { getServerDateOnly } from "@/lib/initial-time-zone"
import { convertLanguageToISOCode } from "@/lib/language-to-iso-code"
import { getSlotLabel } from "@/lib/slot"
import { cn } from "@/lib/utils"
import { useSteeringLevelsCapacityQuery } from "@/modules/admin/capacity/services/useSteeringLevelsCapacityQuery"
import { CapacityUpdateDialog } from "@/modules/productivity-cockpit/components/capacity-update-dialog"
import {
  generatePlanningChartData,
  generatePlanningChartOptions,
  PlanningChartTooltip,
} from "@/modules/productivity-cockpit/helpers/build-planning-chart"
import { mapSlotCapacityPlans } from "@/modules/productivity-cockpit/helpers/get-slots"
import {
  CapacityPlans,
  useCapacityPlansQuery,
  UseCapacityPlansQueryType,
  usePreloadCapacityPlansQuery,
} from "@/modules/productivity-cockpit/services/useCapacityPlansQuery"

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  LineController,
  Title,
  BarElement,
  BarController,
  PlanningChartTooltip,
)

ChartJS.defaults.font.family = "Inter"
ChartJS.defaults.font.size = 12
ChartJS.defaults.color = "#57606E"

export type UpdateCapacity = {
  index: number
  datasetIndex: number
  clientX: number
  clientY: number
}

interface PlanningChartInternalProps {
  today: DateOnly
  queryRef: PreloadedQuery<UseCapacityPlansQueryType>
  Calendar: React.ReactNode
  selectedDate: DateOnly
  isPending: boolean
}

const PlanningChart = () => {
  const today = useRef(getServerDateOnly()).current
  const [queryReference, loadQuery] = useCapacityPlansQuery()

  const [selectedDate, setSelectedDate] = useState<DateOnly>(today)
  const [isPending, startTransition] = useTransition()

  if (!queryReference) loadQuery({ date: selectedDate.toString() })

  const isGreaterOrEqualThanTodayAndLowerThan7Days = (date: DateOnly) => {
    return date.isGreaterOrEqualThan(today) && date.isLowerThan(today.addDays(7))
  }

  const isGreaterOrEqualThanToday = selectedDate.addDays(-1).isGreaterOrEqualThan(today)
  const isInTheNext7Days = selectedDate.isLowerThan(today.addDays(6))

  const handleUpdateDate = (date: DateOnly) => {
    setSelectedDate(date)
    startTransition(() => loadQuery({ date: date.toString() }))
  }

  const CalendarInternal = () => (
    <div className="flex items-center gap-4 py-2.5" data-testid="planning-chart-date-picker">
      <DatePicker
        disabledMatcher={(d) => !isGreaterOrEqualThanTodayAndLowerThan7Days(new DateOnly(d))}
        readOnly={false}
        data-testid="selected-date"
        placeholder={VISUAL_DATE_FORMAT}
        value={selectedDate.toDate()}
        today={today.toDate()}
        className="h-12"
        onSelect={(date) => handleUpdateDate(new DateOnly(date))}
        onReset={() => handleUpdateDate(today)}
        wrapperClassName="w-57.5"
        errorClasses="truncate"
        translateError
      />
      <div className="flex items-center gap-2">
        <Button
          variant="tertiary"
          className="p-2"
          data-testid="left-arrow-button"
          onClick={() => handleUpdateDate(selectedDate.addDays(-1))}
          disabled={!isGreaterOrEqualThanToday}>
          <CaretLeft
            className={cn("h-6 w-6", isGreaterOrEqualThanToday ? "text-on-surface" : "text-on-surface-disabled")}
          />
        </Button>
        <Button
          variant="tertiary"
          className="p-2"
          data-testid="right-arrow-button"
          onClick={() => handleUpdateDate(selectedDate.addDays(1))}
          disabled={!isInTheNext7Days}>
          <CaretRight className={cn("h-6 w-6", isInTheNext7Days ? "text-on-surface" : "text-on-surface-disabled")} />
        </Button>
      </div>
    </div>
  )

  return (
    <Suspense fallback={<LoadingSpinner isFullPage size={24} />}>
      {queryReference ? (
        <PlanningChartInternal
          queryRef={queryReference}
          today={today}
          isPending={isPending}
          selectedDate={selectedDate}
          Calendar={<CalendarInternal />}
        />
      ) : null}
    </Suspense>
  )
}

const PlanningChartInternal = ({ Calendar, selectedDate, isPending, today, queryRef }: PlanningChartInternalProps) => {
  const chartRef = useRef<HTMLDivElement | null>(null)

  const [updateCapacity, setUpdateCapacity] = useState<UpdateCapacity | null>(null)

  const data = usePreloadCapacityPlansQuery(queryRef)

  const currentSlotId = data.capacityPlans.find((slot) => slot.isCurrentSlot)?.slotId
  const isToday = selectedDate.equals(today)

  const chartData = useMemo(() => {
    const slotCapacityPlans = mapSlotCapacityPlans(data)
    return generatePlanningChartData(slotCapacityPlans, updateCapacity, isToday)
  }, [updateCapacity, data, isToday])

  return (
    <div className="planning-chart flex h-full flex-col" ref={chartRef}>
      <div className="flex h-full w-full">
        <div data-testid="planning-chart" className="w-full min-w-250">
          {Calendar}
          {chartData && !isPending ? (
            <Chart
              type="bar"
              className="h-64"
              data={chartData}
              options={generatePlanningChartOptions(chartData, setUpdateCapacity, isToday, currentSlotId)}
            />
          ) : (
            <LoadingSpinner isFullPage className="[&>svg]:-mt-37.5" size={24} />
          )}
        </div>
        <SlotsInformation data={data} />
      </div>
      <SteeringLevelWaitingTime />
      {updateCapacity && (
        <CapacityUpdateDialog
          chartRef={chartRef}
          updateCapacity={updateCapacity}
          setUpdateCapacity={setUpdateCapacity}
          chartData={chartData}
          data={data}
        />
      )}
    </div>
  )
}

const SlotsInformation = ({ data }: { data: CapacityPlans }) => {
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (ref.current) {
      const currentSlotElement = ref.current.querySelector(".current-slot")
      if (currentSlotElement) {
        currentSlotElement.scrollIntoView()
      }
    }
  }, [])

  return (
    <ScrollArea
      data-testid="slot-information"
      className="h-full w-full max-w-70"
      viewportClassName="max-h-none"
      ref={ref}>
      {data.capacityPlans.map((slot, index) => (
        <div key={slot.id} className="flex flex-col gap-1 px-4 py-2">
          <div className={cn("text-md text-on-surface", slot.isCurrentSlot && "current-slot font-bold")}>
            {data.capacityPlans.length - 1 !== index
              ? `${getSlotLabel(index)} - ${getSlotLabel(index + 1)}`
              : `${getSlotLabel(index)} - ${getSlotLabel(0)}`}
          </div>
          <span
            className={cn("text-on-surface-subdued text-xs whitespace-pre-wrap", slot.isCurrentSlot && "font-medium")}>
            {slot.explanation}
          </span>
        </div>
      ))}
    </ScrollArea>
  )
}

const SteeringLevelWaitingTime = () => {
  const { t } = useTranslation()
  const response = useSteeringLevelsCapacityQuery()
  const { steeringLevels } = response

  const steeringLevelTexts = steeringLevels
    .map((s) => `${convertLanguageToISOCode(s.language)}: ${s.levelValue}`)
    .join(", ")

  const waitingTimeTexts = steeringLevels
    .map(
      (s) =>
        `${convertLanguageToISOCode(s.language)}: ${s.waitingTime.status === "EXCEEDS_MAXIMUM" ? "> " : ""}${s.waitingTime.minutesToWait / 60}h`,
    )
    .join(", ")

  return (
    <div className="text-on-surface-subdued mt-12 flex w-full justify-center gap-2 text-sm leading-5 font-normal">
      <span>{t("admin.capacity.steeringLevel.title")}:</span>
      <span data-testid="steering-level-card-content">{steeringLevelTexts}</span>
      <span>{t("admin.capacity.steeringLevel.waitingTimeLabel")}:</span>
      <span data-testid="steering-level-card-content">{waitingTimeTexts}</span>
    </div>
  )
}

export { PlanningChart }
